import { FormTabType, TabContext } from 'components/common/form/tab';
import { IFormTabComponentRecord } from 'components/common/form/formTools';
import { AssocArray } from 'tools/types';
import React, { useContext, useMemo } from 'react';
import { FormElementComponent } from 'components/common/form/element/FormElementComponent';
import { FormLayoutContext, ITabContextProps, ITabProps } from 'components/common/form/layout/tab/index';
import FormTabContentDefaultStacks from 'components/common/form/layout/tab/FormTabContentDefaultStacks';
import { Box, Stack } from '@mui/material';
import AnchorNavigation from 'components/anchor-navigation/AnchorNavigation';

function FormTabsContentWrapper({ children }: { children: any }) {
    const layoutContext = useContext(FormLayoutContext);

    if (layoutContext.formLayout == 'popup') {
        return (
            <Stack
                alignItems="center"
                className={
                    'default-components form-components-indent scroll-content-container scroll-content-container--form-popup'
                }
                sx={{
                    width: '100%',
                }}
            >
                {children}
            </Stack>
        );
    }

    return <>{children}</>;
}

export default function FormTabsContent({
    componentsMap,
    formTabs,
    elementProps,
    customTabs = {},
}: ITabContextProps & {
    formTabs: Array<FormTabType>;
    componentsMap: Array<IFormTabComponentRecord>;
    customTabs?: AssocArray<React.JSX.Element>;
}) {
    return (
        <FormTabsContentWrapper>
            {formTabs.map((tab: FormTabType, i: number) => (
                <FormTabItem
                    key={i}
                    tab={tab}
                    elementProps={elementProps}
                    componentsMap={componentsMap}
                    customTab={customTabs[tab.name]}
                />
            ))}
        </FormTabsContentWrapper>
    );
}

function FormTabItem({
    tab,
    elementProps,
    componentsMap,
    customTab,
}: {
    tab: FormTabType;
    elementProps: ITabContextProps['elementProps'];
    componentsMap: Array<IFormTabComponentRecord>;
    customTab?: React.JSX.Element;
}) {
    const layoutContext = useContext(FormLayoutContext);

    const tabContextValue = useMemo(
        // `tab` can keep the same object identity across an isActive toggle (useFormTabs mutates it in
        // place on tab switch), so isActive must be a dependency in its own right, not just via `tab`.
        () => ({ ...tab, isActive: layoutContext.formLayout == 'popup' ? true : tab.isActive }),
        [tab, tab.isActive, layoutContext.formLayout],
    );

    const content = useMemo(() => {
        if (customTab) {
            return <>{customTab}</>;
        }
        return (
            <RegularTabContent
                tabName={tab.name}
                isTabActive={tab.isActive}
                elementProps={elementProps}
                componentsMap={componentsMap}
            />
        );
    }, [tab.isActive, tab.name, elementProps.form?.formDidMount, componentsMap, layoutContext.formLayout, customTab]);

    return <TabContext.Provider value={tabContextValue}>{content}</TabContext.Provider>;
}

function RegularTabContent(tabProps: ITabProps & { componentsMap: Array<IFormTabComponentRecord> }) {
    const layoutContext = useContext(FormLayoutContext);
    const tabComponents = tabProps.componentsMap.filter(
        (record) => (tabProps.tabName == '' || record.tabName == tabProps.tabName) && !record.isNested,
    );

    const getElement = (record: IFormTabComponentRecord, index: number) => (
        <FormElementComponent
            elementProps={tabProps.elementProps}
            record={record}
            formLayout={layoutContext.formLayout}
            formType={layoutContext.formType}
            key={`${record.props.component.uid}_${index}`}
        />
    );

    return (
        <FormTabContentDefaultStacks {...tabProps}>
            <>
                {tabComponents.filter((record) => record.props.component.isPinned).map(getElement)}
                {/*
                {layoutContext.formLayout == 'page' && (
                    <>
                        <AnchorNavigation tabName={tabProps.tabName} isTabActive={tabProps.isTabActive} />
                    </>
                )}*/}
            </>
            <>{tabComponents.filter((record) => !record.props.component.isPinned).map(getElement)}</>
        </FormTabContentDefaultStacks>
    );
}
